import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../context/userContext';
import {
  FilterColumn,
  SortColumn,
  SortDirection,
  TableState,
  TableStatus,
} from '../components/Common/DataTable/DataTable';
import { CountList } from '../types/appModels';
import { DataError } from '../types/appTypes';

export type UseTableDataArgs<T> = {
  baseEndpoint: string;
  method?: string;
  initialData?: CountList<T>;
  initialStatus?: TableStatus;
  defaultSort?: SortColumn;
  defaultFilters?: FilterColumn[];
  buildDefaultFilters?: () => FilterColumn[];
  defaultSkip?: number;
  defaultLimit?: number;
  onError?: (error: any) => void;
};

export function useTableData<T>({
  baseEndpoint,
  method = 'GET',
  initialData = undefined,
  initialStatus = TableStatus.Idle,
  defaultSort = undefined,
  defaultFilters = undefined,
  buildDefaultFilters = undefined,
  defaultSkip = undefined,
  defaultLimit = undefined,
  onError = undefined,
}: UseTableDataArgs<T>) {
  const { user } = useContext(UserContext);
  const [dataState, setDataState] = useState<TableState<T>>({
    status: initialStatus,
    error: undefined,
    data: initialData,
    sort: defaultSort,
    filters:
      buildDefaultFilters == null ? defaultFilters : buildDefaultFilters(),
  });

  const buildQuery = (
    skipAmt?: number,
    limitAmt?: number,
    sortCol?: SortColumn,
    filterCols?: FilterColumn[]
  ): string => {
    const qsParams: string[] = [];
    if (sortCol !== undefined) {
      qsParams.push(`sort=${sortCol.columnId}`);
      qsParams.push(
        `sortDirection=${
          sortCol.direction === SortDirection.Descending ? -1 : 1
        }`
      );
    }

    if (filterCols !== undefined && filterCols.length > 0) {
      filterCols.forEach((fc) => {
        // Handle date range filters specially
        if (fc.columnId.endsWith('From') || fc.columnId.endsWith('To')) {
          qsParams.push(`${fc.columnId}=${encodeURIComponent(fc.filter || '')}`);
        } else {
          qsParams.push(`${fc.columnId}=${encodeURIComponent(fc.filter || '')}`);
        }
      });
    }

    if (skipAmt !== undefined) qsParams.push(`skip=${skipAmt}`);
    if (limitAmt !== undefined) qsParams.push(`limit=${limitAmt}`);

    return qsParams.join('&');
  };

  const appendData = (od?: CountList<T>, nd?: CountList<T>) => {
    if (od === undefined || od.list.length === 0) return nd;
    if (nd === undefined || nd.list.length === 0) return od;
    return { count: nd.count, list: [...od.list, ...nd.list] };
  };

  const runFetch = (
    skipOverride?: number,
    limitOverride?: number,
    sortOverride?: SortColumn,
    filterOverride?: FilterColumn[],
    append?: boolean
  ) => {
    skipOverride ??= defaultSkip;
    limitOverride ??= defaultLimit;
    sortOverride ??= defaultSort;
    filterOverride ??= defaultFilters;

    // If no filterOverride is provided, use default date filter to limit to last 24 hours
    if (!filterOverride || filterOverride.length === 0) {
      if (dataState.filters && dataState.filters.length > 0) {
        filterOverride = dataState.filters;
      } else {
        // Add default date filter for the last 24 hours
        const oneDayAgo = new Date();
        oneDayAgo.setDate(oneDayAgo.getDate() - 1);
        
        filterOverride = [
          {
            columnId: 'dateTimeFrom',
            filter: oneDayAgo.toISOString()
          }
        ];
      }
    }

    const query: string = buildQuery(
      skipOverride,
      limitOverride,
      sortOverride,
      filterOverride
    );
    const sep: string = baseEndpoint.indexOf('?') >= 0 ? '&' : '?';
    const endpoint = `${baseEndpoint}${sep}${query}`;

    console.log('Fetching logs with query:', endpoint); // Debug log

    let headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (user !== undefined && user !== null) {
      headers = { ...headers, Authorization: `Bearer ${user.accessToken}` };
    }

    fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
      method,
      headers,
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Server error (${response.status}): ${errorText}`);
        }
        return response.text(); // Use text() first to debug any JSON parsing issues
      })
      .then(responseText => {
        try {
          // Try to parse the response as JSON
          const data = JSON.parse(responseText);
          
          setDataState((pv) => {
            let retData: CountList<T> | undefined;
            if (append) {
              retData = appendData(pv.data, data);
            } else {
              retData = data;
            }

            return {
              ...pv,
              status: TableStatus.Idle,
              error: undefined,
              data: retData,
            };
          });
        } catch (parseError) {
          console.error('JSON parsing error:', parseError, 'Raw response:', responseText);
          throw new Error(`Invalid JSON response: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`);
        }
      })
      .catch((err) => {
        console.error('Error fetching data:', err);
        setDataState((pv) => {
          return { 
            ...pv, 
            status: TableStatus.Error, 
            error: {
              code: 'FETCH_ERROR',
              message: err.message || 'An error occurred while fetching data',
              status: 500
            } 
          };
        });
        
        if (onError) {
          onError(err);
        }
      });
  };

  useEffect(() => {
    setDataState((pv) => {
      return { ...pv, status: TableStatus.Loading };
    });

    runFetch();
  }, [user, method, baseEndpoint]);

  const setSort = (sort?: SortColumn, status?: TableStatus) => {
    setDataState((pv) => {
      return { ...pv, status: status === undefined ? pv.status : status, sort };
    });
  };

  const runSort = (sort?: SortColumn) => {
    setSort(sort, TableStatus.Sorting);
    runFetch(0, dataState.data?.list.length, sort, dataState.filters, false);
  };

  const setFilters = (filters: FilterColumn[], status?: TableStatus) => {
    // If filters is empty array, clear all filters
    if (filters.length === 0) {
      setDataState((pv) => {
        return {
          ...pv,
          status: status === undefined ? pv.status : status,
          filters: [],
        };
      });
      return [];
    }

    // Otherwise merge with existing filters
    let stateFilters = dataState.filters;
    if (stateFilters === undefined) {
      stateFilters = [];
    }

    for (let filter of filters) {
      if (stateFilters.length > 0) {
        const i = stateFilters.findIndex(
          (fc) => fc.columnId === filter.columnId
        );

        if (i >= 0) {
          stateFilters!.splice(i, 1);
        }
      }

      stateFilters.push(filter);
    }

    setDataState((pv) => {
      return {
        ...pv,
        status: status === undefined ? pv.status : status,
        filters: stateFilters,
      };
    });

    return stateFilters;
  };

  const runFilters = (filters: FilterColumn[]) => {
    const stateFilters = setFilters(filters, TableStatus.Filtering);
    runFetch(0, defaultLimit, dataState.sort, stateFilters, false);
  };

  const loadPage = () => {
    setDataState((pv) => {
      return { ...pv, status: TableStatus.Loading };
    });

    runFetch(
      dataState.data?.list.length,
      defaultLimit,
      dataState.sort,
      dataState.filters,
      true
    );
  };

  const reload = () => {
    setDataState((pv) => {
      return { ...pv, status: TableStatus.Loading };
    });

    runFetch(
      0,
      dataState.data?.count,
      dataState.sort,
      dataState.filters,
      false
    );
  };

  const startAction = () => {
    setDataState((pv) => {
      return { ...pv, status: TableStatus.Acting };
    });
  };

  const endAction = () => {
    setDataState((pv) => {
      return { ...pv, status: TableStatus.Idle };
    });
  };

  const actionFailed = (status: TableStatus, error?: DataError) => {
    setDataState((pv) => {
      return { ...pv, status, error };
    });
  };

  const setState = (action: React.SetStateAction<TableState<T>>) => {
    setDataState(action);
  };

  return {
    dataState,
    setState,
    setSort,
    runSort,
    setFilters,
    runFilters,
    loadPage,
    reload,
    startAction,
    endAction,
    actionFailed,
  };
}